
import VueFilters from "@/vue-filters";
import { Component, Prop, Vue } from "vue-property-decorator";
import { BvTableFieldArray } from "bootstrap-vue";
import {
  ICountryConfig,
  IDeclaredValue,
  IDeclaredValuePack,
  IDeclaredValuePackFingerprint,
  ISolCustomerCurrency,
  ISolCustomerDenominationPack,
  ISolCustomerPack,
  IUserClientSubclientServicepointData,
  IUserDetailData,
  IValuePack,
  SolCustomerWoStatusCode,
  IAmountByCurrency,
} from "@/entities";
import { mapGetters } from "vuex";
import IconTrash from "@/components/common/icon/IconTrash.vue";
import { Getter } from "vuex-class";
import IconEdit from "@/components/common/icon/IconEdit.vue";
import { IDeleteDeclaredValueRequestBody } from "@/services";
import { SweetAlertResult } from "sweetalert2";
import ClientsFilter from "@/components/client/ClientsFilter.vue";
import IconQr from "@/components/common/icon/IconQr.vue";
import DeclaredValuePackDetails from "@/components/orders/agile_pickup/DeclaredValuePackDetails.vue";
import AddEditDeclaredValuePackModal from "@/components/orders/agile_pickup/AddEditDeclaredValuePackModal.vue";

@Component({
  name: "agile-pickup-declared-value-pack-table",
  mixins: [VueFilters],
  computed: {
    ...mapGetters(["getLocale"]),
  },
  components: {
    AddEditDeclaredValuePackModal,
    DeclaredValuePackDetails,
    IconQr,
    ClientsFilter,
    IconEdit,
    IconTrash,
  },
})
export default class AgilePickupDeclaredValuePackTable extends Vue {
  @Prop({ required: true, default: "", type: String }) serviceCode!: string;
  @Prop({ required: true }) agilePickUpAvailable!: boolean;
  @Prop({ required: true, default: "", type: String }) serviceStatusCode!: string;
  @Prop({ required: false, type: Number }) woStatusCode?: number;
  @Prop({ required: true }) declaredValues!: IDeclaredValuePackFingerprint | null;
  @Prop({ required: true }) loading!: boolean;
  @Prop({ required: true }) packs!: ISolCustomerPack[];
  @Prop({ required: false, default: null })
  servicepointSelected!: IUserClientSubclientServicepointData | null;
  @Prop({ required: false, default: () => [] })
  requestedAmountByCurrency!: IAmountByCurrency[];

  @Getter("getLoggedUser") getLoggedUser!: IUserDetailData;
  @Getter("getCountryConfig") countryConfig!: ICountryConfig;
  @Getter("getSolCustomerCurrencies") solCustomerCurrencies!: ISolCustomerCurrency[];

  public selectedDeclaredValue: IDeclaredValuePack | null = null;

  public collapsed = true;

  get canAddOrEditDeclaredValues(): boolean {
    if (this.woStatusCode != undefined) {
      return this.woStatusCode < SolCustomerWoStatusCode.IN_EXECUTION;
    }

    // Si woStatusCode no está definido, se controla solo por serviceStatusCode
    return ["PENDING", "RECEIVED", "PREPARING", "PREPARED", "PLANNED"].includes(
      this.serviceStatusCode
    );
  }

  get fields(): BvTableFieldArray {
    return [
      {
        key: "actions-left",
        label: "",
        sortable: false,
        thClass: "table-header table-header-active pl-4",
        tdClass: "table-cell-first align-middle position-relative",
      },
      {
        key: "co",
        label: this.$t("column.code") as string,
        sortable: false,
        thClass: "table-header",
        tdClass: "table-cell-middle align-middle",
      },
      {
        key: "vl",
        label: this.$t("column.declaredTotal") as string,
        sortable: false,
        thClass: "table-header",
        tdClass: "table-cell-middle align-middle",
      },
      {
        key: "actions-right",
        label: "",
        sortable: false,
        thClass: "table-header",
        tdClass: "table-cell-last align-middle",
      },
    ];
  }

  async refreshData(): Promise<void> {
    this.$emit("refresh");
  }

  getCurrencySymbol(currencyIsoCode: string): string {
    const currency = this.solCustomerCurrencies.find(
      (currency) => currency.isoCode === currencyIsoCode
    );
    return currency?.symbol || "";
  }

  openNewDeclaredValueModal(): void {
    this.selectedDeclaredValue = null;
    this.$nextTick(() => {
      this.$bvModal.show("addEditDeclaredValuePackModal");
    });
  }
  onClickEdit(declaredValue: IDeclaredValue): void {
    this.selectedDeclaredValue = declaredValue;
    this.$nextTick(() => {
      this.$bvModal.show("addEditDeclaredValuePackModal");
    });
  }

  sumAmounts(
    values: IValuePack[] | undefined
  ): { amount: number; currencySymbol: string; currencyIsoCode: string }[] {
    const amounts: { amount: number; currencySymbol: string; currencyIsoCode: string }[] = [];

    values?.forEach((valuePack) => {
      const packQuantity = valuePack?.pq || 0;
      const packCode = valuePack?.pc || "";
      const currencyIsoCode = valuePack?.cic || "";
      const currencySymbol = this.getCurrencySymbol(currencyIsoCode);

      if (packQuantity && packCode) {
        const pack = this.packs.find((p) => p.code === packCode);

        if (pack) {
          let amountByCurrency = amounts.find((a) => a.currencySymbol === currencySymbol);

          if (amountByCurrency) {
            amountByCurrency.amount += packQuantity * this.sumDenominations(pack.denominations);
          } else {
            amounts.push({
              amount: packQuantity * this.sumDenominations(pack.denominations),
              currencySymbol: currencySymbol,
              currencyIsoCode: currencyIsoCode,
            });
          }
        }
      }
    });
    return amounts;
  }

  sumDenominations(denominations: ISolCustomerDenominationPack[] | undefined): number {
    let amount = 0;

    denominations?.forEach(
      (denomination) => (amount += (denomination?.quantity || 0) * (denomination?.value || 0))
    );

    return amount;
  }

  async modalConfirm(): Promise<SweetAlertResult> {
    return this.$swal({
      title: this.$t("message.deleteDeclaredValue.confirm.title").toString(),
      html: this.$t("message.deleteDeclaredValue.confirm.description").toString(),
      icon: "question",
      showCancelButton: true,
      confirmButtonText: this.$t("label.yes").toString(),
      cancelButtonText: this.$t("label.no").toString(),
    });
  }

  async onClickDelete(declaredValue: IDeclaredValue): Promise<void> {
    const { isConfirmed } = await this.modalConfirm();
    if (isConfirmed) {
      const body: IDeleteDeclaredValueRequestBody = {
        countryCode: this.getLoggedUser.country!.code!,
        serviceCode: this.serviceCode,
        declaredCode: [{ code: declaredValue.co! }],
      };
      let success = await this.$services.solServiceApi.deleteDeclaredValue(body);

      if (success) {
        await this.refreshData();
      }
    }
  }
}
